import React, { useEffect, useState, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import {
  Calendar,
  Home,
  MessageSquare,
  ShoppingBag,
  User,
  LogOut,
  MessageCircle,
} from 'lucide-react';
import axios from 'axios';
import TrripLogo from '../../assets/Trrip_logo/trrip_logo.png';
import MobileNav from './DashboardComponents/MobileNav';
import ImageGallery from './DashboardComponents/ImageGallery';
import PartnerNavbar from '../../components/Dashboard/PartnerNavbar';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('');
  const [userData, setUserData] = useState({
    business_name: '',
    owner_name: '',
    phone_number: '',
    email: '',
    account_number: '',
    ifsc_code: '',
    bank_name: '',
    branch_name: '',
    address: '',
    single_line_address: '',
    city: '',
    pincode: '',
    businessHours: {
      openingTime: '',
      closingTime: '',
      days: []
    },
    commission: '',
    image_url: [],
    logo_url: '',
    description: '',
    title: ''
  });
  const [galleryConfig, setGalleryConfig] = useState({ isOpen: false, images: [], index: 0 });
  const [walletBalance, setWalletBalance] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const formDataRef = useRef({});

  useEffect(() => {
    const path = location.pathname.split('/activity/')[1] || '';
    setActiveTab(path || 'home');
  }, [location]);

  const fetchUserData = async () => {
    const token = localStorage.getItem('token_partner_activities');
    const id = localStorage.getItem('id_partner_activities');
    
    if (!token || !id) {
      navigate('/login');
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/auth/profile/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserData(response.data);
      formDataRef.current = response.data;
      
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const partnerId = tokenPayload.business_id;
        const walletRes = await axios.get(`${process.env.REACT_APP_BASE_URL}/wallet/Activity/${partnerId}`);
        if (walletRes.data.success) {
          setWalletBalance(walletRes.data.balance);
        }
      } catch (err) {
        console.error('Error fetching wallet balance:', err);
      }
    } catch (error) {
      console.error('Error fetching activity data:', error);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token_partner_activities');
    localStorage.removeItem('id_partner_activities');
    localStorage.removeItem('roleid');
    navigate('/login');
  };

  const NavLink = ({ icon: Icon, label, value, path }) => (
    <button
      onClick={() => navigate(`/activity/${path}`)}
      className={`flex items-center w-full gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 group ${activeTab === value
        ? 'bg-[#067978] text-white shadow-lg shadow-teal-900/20'
        : 'text-gray-500 hover:bg-gray-50 hover:text-[#067978]'
        }`}
    >
      <Icon size={20} className={`${activeTab === value ? 'text-white' : 'text-gray-400 group-hover:text-[#067978]'} transition-colors`} />
      <span>{label}</span>
    </button>
  );

  return (
    <div className="h-screen w-screen overflow-hidden bg-slate-50 flex flex-col font-sans">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Modern Partner Header Navbar */}
      <PartnerNavbar
        portalType="activity"
        subtitle="Activity Management"
        userData={userData}
        walletBalance={walletBalance}
        onMenuClick={() => setIsMobileMenuOpen(true)}
      />

      <div className="flex flex-1 overflow-hidden">
        <aside className="hidden lg:flex w-64 bg-white border-r border-gray-100 flex-col flex-shrink-0 overflow-hidden select-none">
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.2em] mb-4 pl-4">Main Menu</p>
            <NavLink icon={Home} label="Dashboard" value="home" path="" />
            <NavLink icon={User} label="Activity Profile" value="profile" path="profile" />
            <NavLink icon={ShoppingBag} label="Activity Items" value="listing" path="listing" />
            <NavLink icon={Calendar} label="Reservations" value="booking" path="booking" />
            <NavLink icon={MessageSquare} label="Guest Reviews" value="reviews" path="reviews" />
            <NavLink icon={MessageCircle} label="Support" value="support" path="support" />
          </nav>
          <div className="p-4 border-t border-gray-100 flex-shrink-0 bg-white">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-xl transition-all group"
            >
              <LogOut size={18} className="text-rose-500 group-hover:scale-110 transition-transform" />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        <MobileNav
          portalType="activity"
          subtitle="Activity Management"
          activeTab={activeTab}
          navigate={(path) => navigate(path ? `/activity/${path}` : '/activity')}
          userData={userData}
          handleLogout={handleLogout}
          isMenuOpen={isMobileMenuOpen}
          setIsMenuOpen={setIsMobileMenuOpen}
        />

        <div id="dashboard-scroll-container" className="flex-1 overflow-y-auto bg-[#F8FAFC]">
          <div className="max-w-[1920px] mx-auto min-h-full px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24 lg:pb-8">
            <Outlet context={{ userData, setUserData, formDataRef, setGalleryConfig }} />
          </div>
        </div>
      </div>

      <ImageGallery
        isOpen={galleryConfig.isOpen}
        onClose={() => setGalleryConfig({ ...galleryConfig, isOpen: false })}
        images={galleryConfig.images}
        initialIndex={galleryConfig.index}
      />
    </div>
  );
};

export default Dashboard;
